// The rules for changing a staff user's ACCESS or turning them off. Pure, so the
// server (inside the transaction) and the tests run the exact same decision.
//
// ⚠️ "ACTIVE ADMIN" means role 'admin' AND not disabled. A disabled admin
// cannot sign in, so counting them would let the last person who CAN get in be
// demoted or turned off.

export const DISABLED_ACCESS_MESSAGE = 'Your Kyck access has been turned off. Ask an admin.';

export type AccessSubject = { uid: string; role: 'admin' | 'staff'; disabled: boolean };

export type AccessChange =
  | { kind: 'setRole'; role: 'admin' | 'staff' }
  | { kind: 'disable' }
  | { kind: 'enable' };

export type AccessRefusal = { status: 409 | 403; message: string };

export function isActiveAdmin(u: AccessSubject): boolean {
  return u.role === 'admin' && !u.disabled;
}

/** Returns why the change must be refused, or null when it is allowed. */
export function checkAccessChange(input: {
  actorUid: string;
  targetUid: string;
  change: AccessChange;
  /** Every staff user, as currently stored (including the target). */
  users: AccessSubject[];
}): AccessRefusal | null {
  const { actorUid, targetUid, change, users } = input;
  const target = users.find((u) => u.uid === targetUid);
  if (!target) return { status: 409, message: 'That user was not found.' };

  const self = actorUid === targetUid;
  if (change.kind === 'setRole' && self && change.role !== target.role) {
    return { status: 403, message: 'You cannot change your own access level.' };
  }
  if (change.kind === 'disable' && self) {
    return { status: 403, message: 'You cannot turn off your own access.' };
  }

  const removesAnAdmin =
    isActiveAdmin(target) &&
    ((change.kind === 'setRole' && change.role !== 'admin') || change.kind === 'disable');
  if (removesAnAdmin) {
    const others = users.filter((u) => u.uid !== targetUid && isActiveAdmin(u)).length;
    if (others === 0) {
      return { status: 409, message: 'There must always be at least one active admin.' };
    }
  }
  return null;
}

/** Throttle for lastSignedInAt: at most one write per 15 minutes per user. */
export const LAST_SIGNED_IN_THROTTLE_MS = 15 * 60_000;

export function shouldTouchLastSignedIn(lastMs: number | null, nowMs: number): boolean {
  if (lastMs === null || !Number.isFinite(lastMs)) return true;
  return nowMs - lastMs >= LAST_SIGNED_IN_THROTTLE_MS;
}
